import React, { useState, useCallback, useMemo } from 'react';
import {
  X, Upload, FileCheck2, AlertTriangle, ArrowRight, ArrowLeft, Info, Loader2,
  Cpu, Activity, Zap, ChevronRight, ShieldCheck, ShieldAlert, ShieldX, Wrench,
  HelpCircle, Check, FileText, Sparkles, Shield,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Backend limits (server.py:738-742). Client-side matches so users get an
// instant error instead of a 413 after a slow upload.
const MAX_BIN_BYTES = 16 * 1024 * 1024; // 16 MB
const MAX_LOG_BYTES = 8 * 1024 * 1024;  // 8 MB

const STEPS = [
  {
    key: 'bin',
    title: 'Stock ECU Binary',
    subtitle: 'Your original, unmodified .bin read from the ECU',
    accept: '.bin',
    icon: Cpu,
    maxBytes: MAX_BIN_BYTES,
    maxLabel: '16 MB max',
    accentClass: 'text-[#FF5E1A]',
    accentBg: 'bg-[#FF5E1A]/15 border-[#FF5E1A]/40',
    help: 'The unmodified factory read from your ECU. Full-size dump.',
    howTo: [
      'Read your ECU with MSV80 QuickFlash, Autotuner, KT200, MPPS, or any bench/boot-mode tool.',
      'MSV80 dumps are 2 MB. Other ECUs may be 1–8 MB.',
      'If you\'ve already flashed a tune, use the stock backup you saved before flashing.',
    ],
    typicalSize: 'Typical size: 1–8 MB',
    dataTestId: 'free-upload-bin',
  },
  {
    key: 'idle',
    title: 'Idle Datalog',
    subtitle: '30–60 seconds of warm idle, in neutral or park',
    accept: '.csv,.log,.txt',
    icon: Activity,
    maxBytes: MAX_LOG_BYTES,
    maxLabel: '8 MB max',
    accentClass: 'text-cyan-400',
    accentBg: 'bg-cyan-500/10 border-cyan-500/40',
    help: 'Reveals fuel trim balance, misfires, vacuum leaks, and temp sensor health.',
    howTo: [
      'Engine fully warmed up (coolant above 180°F / 82°C).',
      'Transmission in Neutral or Park. AC off, headlights off, no accessories.',
      'Record 30–60 seconds of steady idle — no revs, no touching the pedal.',
      'Use any OBD-II datalogger: INPA, ISTA, HP Tuners, Cobb, BimmerCode, Torque Pro.',
    ],
    typicalSize: 'Typical size: 30–400 KB',
    dataTestId: 'free-upload-idle',
  },
  {
    key: 'pull',
    title: '3rd Gear WOT Pull',
    subtitle: 'Full throttle from ~2,500 RPM to redline, in 3rd gear',
    accept: '.csv,.log,.txt',
    icon: Zap,
    maxBytes: MAX_LOG_BYTES,
    maxLabel: '8 MB max',
    accentClass: 'text-emerald-400',
    accentBg: 'bg-emerald-500/10 border-emerald-500/40',
    help: 'Reveals real peak power, AFR under load, knock events, and timing headroom.',
    howTo: [
      'Safe, flat, empty road. Traction/DSC OFF for a clean pull.',
      '3rd gear only — no shifts mid-pull.',
      'Roll on at ~2,500 RPM, then pedal 100% all the way to redline.',
      'Keep the pull at least 4 seconds of continuous WOT.',
      'Record with the same tool you used for idle.',
    ],
    typicalSize: 'Typical size: 100–800 KB',
    dataTestId: 'free-upload-pull',
  },
];

const TOTAL_STEPS = STEPS.length;

// ─────────────────────────────────────────────────────────────
// UI atoms
// ─────────────────────────────────────────────────────────────

function StepDots({ current, uploaded }) {
  return (
    <div className="flex items-center gap-1.5" data-testid="wizard-stepper">
      {STEPS.map((s, i) => {
        const done = uploaded[s.key];
        const active = i === current;
        return (
          <React.Fragment key={s.key}>
            <div
              className={`flex items-center gap-2 transition-all ${active ? 'flex-1' : ''}`}
              data-testid={`stepper-${s.key}${active ? '-active' : ''}`}
            >
              <span
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold font-['JetBrains_Mono'] transition-all ${
                  done
                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/50'
                    : active
                      ? 'bg-[#FF5E1A] text-white shadow-[0_0_16px_rgba(255,94,26,0.5)]'
                      : 'bg-[#0A0A0A] text-zinc-600 border border-zinc-800'
                }`}
              >
                {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </span>
              {active && (
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-white truncate hidden sm:inline">
                  {s.title}
                </span>
              )}
            </div>
            {i < STEPS.length - 1 && (
              <span
                className={`h-px flex-1 max-w-[24px] sm:max-w-[40px] ${
                  done ? 'bg-emerald-500/40' : 'bg-zinc-800'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function LiabilityBanner() {
  return (
    <div
      className="bg-gradient-to-r from-amber-950/40 via-red-950/30 to-amber-950/40 border border-amber-500/40 rounded-sm px-4 py-2.5 flex items-start gap-3"
      data-testid="liability-disclaimer"
    >
      <Shield className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
      <div className="text-[11px] leading-relaxed">
        <p className="text-amber-200 font-bold uppercase tracking-wider text-[10px]">Experimental tuning — you assume all risk.</p>
        <p className="text-amber-100/80">Stock backup always saved. ECU restoration available.</p>
      </div>
    </div>
  );
}

function DropZone({ step, file, onFile, error }) {
  const [drag, setDrag] = useState(false);

  const handleFile = useCallback((f) => {
    if (!f) return;
    onFile(f);
  }, [onFile]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const Icon = step.icon;

  return (
    <label
      className={`block border-2 border-dashed rounded-sm p-6 sm:p-8 cursor-pointer transition-all ${
        error
          ? 'border-red-500/50 bg-red-500/5'
          : file
            ? 'border-emerald-500/50 bg-emerald-500/5'
            : drag
              ? 'border-[#FF5E1A] bg-[#FF5E1A]/10 scale-[1.01]'
              : 'border-zinc-700 hover:border-[#FF5E1A]/60 hover:bg-[#FF5E1A]/[0.03] bg-[#0A0A0A]'
      }`}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={handleDrop}
      data-testid={step.dataTestId}
    >
      <input
        type="file"
        accept={step.accept}
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${step.dataTestId}-input`}
      />
      {file ? (
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-sm border ${step.accentBg} flex items-center justify-center flex-shrink-0`}>
            <FileCheck2 className="w-6 h-6 text-emerald-400" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-white font-['JetBrains_Mono'] text-sm truncate" data-testid={`${step.dataTestId}-name`}>
              {file.name}
            </p>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              {(file.size / 1024).toFixed(1)} KB · click or drop to change
            </p>
          </div>
          <Check className="w-5 h-5 text-emerald-400 flex-shrink-0" />
        </div>
      ) : (
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-sm border ${step.accentBg} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-6 h-6 ${step.accentClass}`} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-white font-semibold text-sm">Drop your {step.accept.split(',')[0]} file here</p>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              or click to browse · accepts {step.accept} · {step.maxLabel}
            </p>
          </div>
          <Upload className="w-5 h-5 text-zinc-500 flex-shrink-0" />
        </div>
      )}
    </label>
  );
}

function ProgressBar({ pct }) {
  const clamped = Math.max(0, Math.min(100, Math.round(pct)));
  return (
    <div
      className="w-full bg-[#0A0A0A] border border-zinc-800 rounded-sm h-3 overflow-hidden relative"
      data-testid="upload-progress-bar"
    >
      <div
        className="h-full bg-gradient-to-r from-[#FF5E1A] via-[#FF7A42] to-emerald-400 transition-[width] duration-200"
        style={{ width: `${clamped}%` }}
        data-testid="upload-progress-fill"
      />
      <span
        className="absolute inset-0 flex items-center justify-center text-[10px] font-bold font-['JetBrains_Mono'] text-white/90"
        data-testid="upload-progress-pct"
      >
        {clamped}%
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Report visualization (kept mostly from previous impl, tightened)
// ─────────────────────────────────────────────────────────────

const Section = ({ title, icon: Icon, children }) => (
  <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-4">
    <h4 className="text-white font-['Outfit'] text-sm font-bold mb-3 flex items-center gap-2">
      <Icon className="w-4 h-4 text-[#FF5E1A]" />{title}
    </h4>
    {children}
  </div>
);

const Metric = ({ label, value, sub }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
    <div className="text-xl text-white font-bold font-['JetBrains_Mono']">{value}</div>
    {sub && <div className="text-[10px] text-zinc-600">{sub}</div>}
  </div>
);

const MiniStat = ({ label, value, range }) => (
  <div className="bg-[#050505] border border-[#1f1f1f] rounded-sm px-2 py-1.5">
    <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
    <div className="text-sm text-white font-['JetBrains_Mono']">{value}</div>
    {range && <div className="text-[9px] text-zinc-600">range {range}</div>}
  </div>
);

const CountPill = ({ count, color, label }) => {
  const map = {
    emerald: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
    amber: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
    red: 'bg-red-500/10 text-red-300 border-red-500/30',
  };
  return (
    <div className={`flex flex-col items-center justify-center min-w-[52px] px-2 py-1 rounded-sm border ${map[color]}`}>
      <span className="text-lg font-bold leading-none font-['JetBrains_Mono']">{count}</span>
      <span className="text-[8px] uppercase tracking-wider opacity-70 mt-0.5">{label}</span>
    </div>
  );
};

const FindingsList = ({ items }) => {
  if (!items?.length) return null;
  return (
    <ul className="text-xs text-zinc-400 space-y-1">
      {items.map((f, i) => {
        const warn = f.startsWith('⚠') || f.toLowerCase().includes('knock') || f.toLowerCase().includes('lean') || f.toLowerCase().includes('leak');
        return (
          <li key={`${i}-${f.slice(0, 20)}`} className={`flex items-start gap-1.5 ${warn ? 'text-yellow-300' : ''}`}>
            {warn ? <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" /> : <span className="text-[#FF5E1A] flex-shrink-0">·</span>}
            <span>{f}</span>
          </li>
        );
      })}
    </ul>
  );
};

function HealthGatePanel({ gate }) {
  if (!gate) return null;
  const verdictTheme = {
    READY_TO_TUNE: { icon: ShieldCheck, border: 'border-emerald-500/50', bg: 'bg-gradient-to-br from-emerald-900/20 to-[#0a1a0f]', text: 'text-emerald-300', badge: 'bg-emerald-500/20 text-emerald-200', label: 'READY TO TUNE' },
    NEEDS_ATTENTION: { icon: ShieldAlert, border: 'border-amber-500/50', bg: 'bg-gradient-to-br from-amber-900/20 to-[#1a130a]', text: 'text-amber-300', badge: 'bg-amber-500/20 text-amber-200', label: 'NEEDS ATTENTION' },
    MUST_FIX_FIRST: { icon: ShieldX, border: 'border-red-500/50', bg: 'bg-gradient-to-br from-red-900/20 to-[#1a0a0a]', text: 'text-red-300', badge: 'bg-red-500/20 text-red-200', label: 'MUST FIX FIRST' },
    INSUFFICIENT_DATA: { icon: HelpCircle, border: 'border-zinc-500/50', bg: 'bg-gradient-to-br from-zinc-800/40 to-[#0a0a0a]', text: 'text-zinc-300', badge: 'bg-zinc-700/40 text-zinc-200', label: 'INSUFFICIENT DATA' },
  };
  const theme = verdictTheme[gate.verdict] || verdictTheme.INSUFFICIENT_DATA;
  const Icon = theme.icon;
  const statusColor = {
    green: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/5',
    yellow: 'text-amber-400 border-amber-500/30 bg-amber-500/5',
    red: 'text-red-400 border-red-500/30 bg-red-500/5',
    unknown: 'text-zinc-500 border-zinc-700/50 bg-zinc-800/20',
  };
  const statusDot = {
    green: 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.7)]',
    yellow: 'bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.7)]',
    red: 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.9)] animate-pulse',
    unknown: 'bg-zinc-600',
  };

  return (
    <div data-testid="health-gate-panel" className={`${theme.bg} ${theme.border} border-2 rounded-sm p-4 space-y-4`}>
      <div className="flex items-center gap-3 flex-wrap">
        <Icon className={`w-9 h-9 ${theme.text} flex-shrink-0`} />
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[9px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm ${theme.badge}`}>{theme.label}</span>
            <span className="text-[10px] text-zinc-500 font-['JetBrains_Mono']">Mechanical Health Gate</span>
          </div>
          <p className={`text-sm font-semibold mt-1 ${theme.text}`}>{gate.headline}</p>
        </div>
        <div className="flex gap-2">
          <CountPill count={gate.counts.green} color="emerald" label="OK" />
          <CountPill count={gate.counts.yellow} color="amber" label="Watch" />
          <CountPill count={gate.counts.red} color="red" label="Fix" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {gate.checks.map((c) => (
          <div key={c.id} data-testid={`health-check-${c.id}`} className={`p-2.5 rounded-sm border ${statusColor[c.status]}`}>
            <div className="flex items-start gap-2">
              <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${statusDot[c.status]}`} />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-1">
                  <p className="text-[11px] font-semibold text-white truncate">{c.label}</p>
                  <span className="text-[9px] text-zinc-600 uppercase font-['JetBrains_Mono']">{c.category}</span>
                </div>
                <p className="text-[10px] text-zinc-400 mt-0.5 leading-relaxed">{c.headline}</p>
                {c.fix && c.status === 'yellow' && (
                  <p className="text-[10px] text-amber-200/80 mt-1.5 italic leading-relaxed">{c.fix}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {gate.must_fix_first?.length > 0 && (
        <div className="bg-red-950/30 border border-red-500/40 rounded-sm p-3">
          <div className="flex items-center gap-2 mb-2">
            <Wrench className="w-3.5 h-3.5 text-red-400" />
            <span className="text-xs font-bold text-red-300">Must fix before tuning</span>
          </div>
          <ul className="space-y-2">
            {gate.must_fix_first.map((m) => (
              <li key={m.id} className="pl-4 border-l-2 border-red-500/40">
                <p className="text-[11px] font-semibold text-red-200">{m.label}: {m.headline}</p>
                {m.fix && <p className="text-[10px] text-red-100/80 mt-0.5 leading-relaxed">{m.fix}</p>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function CompactSummary({ report }) {
  const { binary, idle, pull, health_gate: gate } = report;
  const verdictLabel = gate?.verdict?.replaceAll('_', ' ') || 'BASELINE READ';
  const verdictColor = gate?.verdict === 'READY_TO_TUNE' ? 'text-emerald-400'
    : gate?.verdict === 'MUST_FIX_FIRST' ? 'text-red-400'
    : gate?.verdict === 'NEEDS_ATTENTION' ? 'text-amber-400'
    : 'text-cyan-400';

  return (
    <div
      className="bg-gradient-to-br from-[#0a0a0a] to-[#111] border border-[#27272A] rounded-sm p-5 space-y-4"
      data-testid="report-compact-summary"
    >
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-[#FF5E1A]" />
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF5E1A]">Free Baseline Analysis</span>
        <span className={`text-[10px] font-bold uppercase tracking-[0.2em] ml-auto ${verdictColor}`}>{verdictLabel}</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="Peak HP" value={pull?.peak_hp_crank ? `${pull.peak_hp_crank.toFixed(0)}` : '—'} sub={pull?.peak_hp_rpm ? `@ ${pull.peak_hp_rpm.toFixed(0)} RPM` : 'HP crank'} />
        <Metric label="Peak Torque" value={pull?.peak_torque ? `${pull.peak_torque.toFixed(0)}` : '—'} sub={pull?.peak_torque_rpm ? `@ ${pull.peak_torque_rpm.toFixed(0)} RPM` : 'lb-ft'} />
        <Metric label="ECU" value={(binary?.ecu_profile || '—').toUpperCase()} sub={binary?.confidence ? `${binary.confidence} conf.` : ''} />
        <Metric label="Max Knock" value={pull?.max_knock_v ? `${pull.max_knock_v.toFixed(2)}V` : '—'} sub={pull?.max_timing ? `${pull.max_timing.toFixed(1)}° peak` : ''} />
      </div>

      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[#1a1a1a]">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-cyan-400 mb-1.5 flex items-center gap-1.5">
            <Activity className="w-3 h-3" /> Idle
          </div>
          <div className="text-xs text-zinc-400 space-y-0.5">
            <div>AFR: <span className="text-white font-['JetBrains_Mono']">{idle?.afr?.mean?.toFixed(2) || '—'}</span></div>
            <div>RPM: <span className="text-white font-['JetBrains_Mono']">{idle?.rpm?.mean?.toFixed(0) || '—'}</span></div>
            <div>Coolant: <span className="text-white font-['JetBrains_Mono']">{idle?.ect?.mean?.toFixed(0) || '—'}°F</span></div>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-emerald-400 mb-1.5 flex items-center gap-1.5">
            <Zap className="w-3 h-3" /> WOT Pull
          </div>
          <div className="text-xs text-zinc-400 space-y-0.5">
            <div>Max RPM: <span className="text-white font-['JetBrains_Mono']">{pull?.max_rpm_seen?.toFixed(0) || '—'}</span></div>
            <div>WOT AFR: <span className="text-white font-['JetBrains_Mono']">{pull?.wot_afr_range?.mean?.toFixed(2) || '—'}</span></div>
            <div>Clean samples: <span className="text-white font-['JetBrains_Mono']">{pull?.clean_wot_samples || 0}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FullReport({ report }) {
  const { binary, idle, pull, recommendations, health_gate: gate } = report;
  return (
    <div className="space-y-5" data-testid="free-analysis-report-full">
      {gate && <HealthGatePanel gate={gate} />}

      <Section title="Binary Analysis" icon={Cpu}>
        <ul className="text-xs text-zinc-400 space-y-1 font-['JetBrains_Mono']">
          <li>Size: <span className="text-white">{binary?.binary_size_mb} MB</span></li>
          <li>Cal block: <span className="text-white">{binary?.cal_block?.start} – {binary?.cal_block?.end}</span></li>
          <li>Discovered: <span className="text-white">{binary?.axes_discovered} axes · {binary?.tables_discovered} tables · {binary?.constants_discovered} constants</span></li>
          {binary?.tables_by_pattern && (
            <li>Pattern breakdown:{' '}
              {Object.entries(binary.tables_by_pattern).map(([p, c]) => (
                <span key={p} className="inline-block mr-2 text-white">{p}:{c}</span>
              ))}
            </li>
          )}
        </ul>
        {binary?.fingerprint_reasoning?.length > 0 && (
          <div className="mt-2 text-xs text-zinc-500 italic">
            Fingerprint: {binary.fingerprint_reasoning.join(' · ')}
          </div>
        )}
      </Section>

      <Section title="Idle Health" icon={Activity}>
        {idle?.error ? (
          <p className="text-xs text-red-400">{idle.error}</p>
        ) : idle?.warning ? (
          <p className="text-xs text-yellow-400">{idle.warning}</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              {idle?.rpm && <MiniStat label="Idle RPM" value={idle.rpm.mean.toFixed(0)} range={`${idle.rpm.min.toFixed(0)}–${idle.rpm.max.toFixed(0)}`} />}
              {idle?.afr && <MiniStat label="AFR" value={idle.afr.mean.toFixed(2)} range={`${idle.afr.min.toFixed(2)}–${idle.afr.max.toFixed(2)}`} />}
              {idle?.ltft_combined && <MiniStat label="LTFT" value={`${idle.ltft_combined.mean >= 0 ? '+' : ''}${idle.ltft_combined.mean.toFixed(1)}%`} range={`${idle.ltft_combined.min.toFixed(1)}–${idle.ltft_combined.max.toFixed(1)}`} />}
              {idle?.ect && <MiniStat label="ECT" value={`${idle.ect.mean.toFixed(0)}°F`} range={`${idle.ect.min.toFixed(0)}–${idle.ect.max.toFixed(0)}`} />}
            </div>
            <FindingsList items={idle?.findings} />
          </>
        )}
      </Section>

      <Section title="3rd Gear Pull" icon={Zap}>
        {pull?.error ? (
          <p className="text-xs text-red-400">{pull.error}</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <MiniStat label="Max RPM" value={`${pull?.max_rpm_seen?.toFixed(0) || '—'}`} />
              <MiniStat label="Max Timing" value={pull?.max_timing ? `${pull.max_timing.toFixed(1)}°` : '—'} />
              {pull?.wot_afr_range && <MiniStat label="WOT AFR" value={pull.wot_afr_range.mean.toFixed(2)} range={`${pull.wot_afr_range.min.toFixed(2)}–${pull.wot_afr_range.max.toFixed(2)}`} />}
              <MiniStat label="Clean WOT" value={`${pull?.clean_wot_samples || 0}`} />
            </div>
            <FindingsList items={pull?.findings} />
          </>
        )}
      </Section>

      <Section title="AI Recommendations" icon={Info}>
        <ul className="text-sm space-y-2">
          {recommendations?.map((r, i) => (
            <li key={`${i}-${r.slice(0, 20)}`} className="flex items-start gap-2 text-zinc-300">
              <ChevronRight className="w-4 h-4 text-[#FF5E1A] flex-shrink-0 mt-0.5" />
              <span>{r}</span>
            </li>
          ))}
        </ul>
      </Section>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main modal — multi-step wizard state machine
// ─────────────────────────────────────────────────────────────

// Phases: 'upload' (currentStep 0..2) → 'submitting' → 'report'
export default function FreeAnalysisModal({ isOpen, onClose, onSignUpCta }) {
  const [files, setFiles] = useState({ bin: null, idle: null, pull: null });
  const [fileErrors, setFileErrors] = useState({ bin: null, idle: null, pull: null });
  const [currentStep, setCurrentStep] = useState(0);
  const [phase, setPhase] = useState('upload'); // upload | submitting | report
  const [uploadPct, setUploadPct] = useState(0);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [showFullReport, setShowFullReport] = useState(false);

  const uploaded = useMemo(() => ({
    bin: !!files.bin, idle: !!files.idle, pull: !!files.pull,
  }), [files]);
  const activeStep = STEPS[currentStep];
  const activeFile = files[activeStep?.key];
  const activeError = fileErrors[activeStep?.key];

  const reset = useCallback(() => {
    setFiles({ bin: null, idle: null, pull: null });
    setFileErrors({ bin: null, idle: null, pull: null });
    setCurrentStep(0);
    setPhase('upload');
    setUploadPct(0);
    setError(null);
    setReport(null);
    setShowFullReport(false);
  }, []);

  const handleFile = useCallback((stepKey, file) => {
    const step = STEPS.find((s) => s.key === stepKey);
    if (!step) return;
    // Extension check
    const accepted = step.accept.split(',').map((x) => x.trim().toLowerCase());
    const name = (file.name || '').toLowerCase();
    if (!accepted.some((ext) => name.endsWith(ext))) {
      setFileErrors((prev) => ({ ...prev, [stepKey]: `File must be ${step.accept}` }));
      return;
    }
    // Size check
    if (file.size > step.maxBytes) {
      const limitMB = (step.maxBytes / 1024 / 1024).toFixed(0);
      setFileErrors((prev) => ({ ...prev, [stepKey]: `File exceeds ${limitMB} MB limit (yours: ${(file.size / 1024 / 1024).toFixed(1)} MB)` }));
      return;
    }
    // OK
    setFileErrors((prev) => ({ ...prev, [stepKey]: null }));
    setFiles((prev) => ({ ...prev, [stepKey]: file }));
  }, []);

  const goNext = () => {
    if (currentStep < TOTAL_STEPS - 1) {
      setCurrentStep((s) => s + 1);
    }
  };
  const goPrev = () => {
    if (currentStep > 0) setCurrentStep((s) => s - 1);
  };

  const runAnalysis = () => {
    setError(null);
    setPhase('submitting');
    setUploadPct(0);

    const fd = new FormData();
    fd.append('stock_bin', files.bin);
    fd.append('idle_datalog', files.idle);
    fd.append('pull_datalog', files.pull);

    // Use XHR for real upload progress (fetch API can't report upload progress)
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/free-analysis`);

    xhr.upload.onprogress = (evt) => {
      if (evt.lengthComputable) {
        // Cap at 90% during upload — remaining 10% is server processing
        const pct = (evt.loaded / evt.total) * 90;
        setUploadPct(pct);
      }
    };
    xhr.upload.onload = () => setUploadPct(92); // Upload finished; waiting on server
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          setUploadPct(100);
          setReport(data);
          setPhase('report');
          try {
            if (data?.session_id) {
              localStorage.setItem('pending_free_analysis_session', data.session_id);
            }
          } catch (e) {
            if (process.env.NODE_ENV === 'development') console.warn('session persist failed', e);
          }
        } catch (e) {
          setError('Analysis succeeded but response was invalid.');
          setPhase('upload');
        }
      } else {
        let detail = `Analysis failed (HTTP ${xhr.status})`;
        try {
          const errBody = JSON.parse(xhr.responseText);
          if (errBody.detail) detail = errBody.detail;
        } catch (_) { /* keep default */ }
        setError(detail);
        setPhase('upload');
      }
    };
    xhr.onerror = () => {
      setError('Network error — please check your connection and try again.');
      setPhase('upload');
    };
    xhr.send(fd);
  };

  if (!isOpen) return null;

  const isLastStep = currentStep === TOTAL_STEPS - 1;
  const canAdvance = uploaded[activeStep.key];
  const allReady = uploaded.bin && uploaded.idle && uploaded.pull;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-start justify-center p-3 sm:p-4 overflow-y-auto"
      data-testid="free-analysis-modal"
    >
      <div className="bg-[#050505] border border-[#27272A] rounded-sm max-w-3xl w-full my-4 sm:my-8 shadow-[0_0_60px_rgba(255,94,26,0.15)]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-[#27272A] gap-3">
          <div className="min-w-0">
            <h2 className="text-base sm:text-lg font-bold text-white font-['Outfit'] truncate">Free Baseline Analysis</h2>
            <p className="text-[11px] sm:text-xs text-zinc-500 mt-0.5 truncate">3 files · no sign-up · AI-powered ECU + datalog read</p>
          </div>
          <button
            onClick={() => { reset(); onClose(); }}
            data-testid="free-analysis-close"
            className="p-2 hover:bg-zinc-800 rounded-sm flex-shrink-0"
          >
            <X className="w-4 h-4 text-zinc-400" />
          </button>
        </div>

        {/* Persistent liability banner across all phases */}
        <div className="px-4 sm:px-5 pt-4">
          <LiabilityBanner />
        </div>

        {/* Stepper — only during upload phase */}
        {phase === 'upload' && (
          <div className="px-4 sm:px-5 pt-4">
            <StepDots current={currentStep} uploaded={uploaded} />
          </div>
        )}

        {/* Body */}
        <div className="p-4 sm:p-5 space-y-5">
          {/* ═══ UPLOAD PHASE ═══ */}
          {phase === 'upload' && (
            <>
              {/* Step header */}
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-sm border ${activeStep.accentBg} flex items-center justify-center flex-shrink-0`}>
                  <activeStep.icon className={`w-5 h-5 ${activeStep.accentClass}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 font-['JetBrains_Mono']">
                      Step {currentStep + 1} of {TOTAL_STEPS}
                    </span>
                    <span className="text-[10px] text-zinc-600">·</span>
                    <span className="text-[10px] text-zinc-500 font-['JetBrains_Mono']">{activeStep.typicalSize}</span>
                  </div>
                  <h3 className="text-lg font-bold text-white font-['Outfit'] mt-0.5">{activeStep.title}</h3>
                  <p className="text-xs text-zinc-400 mt-1">{activeStep.subtitle}</p>
                </div>
              </div>

              {/* Drop zone */}
              <DropZone
                step={activeStep}
                file={activeFile}
                onFile={(f) => handleFile(activeStep.key, f)}
                error={activeError}
              />

              {activeError && (
                <div
                  className="bg-red-500/10 border border-red-500/40 rounded-sm p-3 text-xs text-red-300 flex items-center gap-2"
                  data-testid="file-error"
                >
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  {activeError}
                </div>
              )}

              {/* How-to guide */}
              <div className="bg-[#0A0A0A] border border-[#1f1f1f] rounded-sm p-4">
                <div className="flex items-center gap-2 mb-2.5">
                  <Info className={`w-3.5 h-3.5 ${activeStep.accentClass}`} />
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400">How to record</span>
                </div>
                <ul className="space-y-1.5">
                  {activeStep.howTo.map((line, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-zinc-400">
                      <span className={`${activeStep.accentClass} font-bold flex-shrink-0 mt-px`}>›</span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Submission-level error banner */}
              {error && (
                <div
                  className="bg-red-500/10 border border-red-500/40 rounded-sm p-3 text-xs text-red-300 flex items-center gap-2"
                  data-testid="submit-error"
                >
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Nav row */}
              <div className="flex items-center justify-between gap-3 pt-2">
                <button
                  onClick={goPrev}
                  disabled={currentStep === 0}
                  className="text-xs text-zinc-400 hover:text-white disabled:text-zinc-700 disabled:cursor-not-allowed flex items-center gap-1.5 px-3 py-2 rounded-sm hover:bg-zinc-800 disabled:hover:bg-transparent transition-colors"
                  data-testid="wizard-prev"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back
                </button>

                {!isLastStep ? (
                  <button
                    onClick={goNext}
                    disabled={!canAdvance}
                    className={`px-5 py-2.5 rounded-sm font-bold text-xs uppercase tracking-wider inline-flex items-center gap-2 transition-all ${
                      canAdvance
                        ? 'bg-[#FF5E1A] hover:bg-[#FF7A42] text-white shadow-[0_0_24px_rgba(255,94,26,0.35)]'
                        : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                    }`}
                    data-testid="wizard-next"
                  >
                    Next Step
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <button
                    onClick={runAnalysis}
                    disabled={!allReady}
                    className={`px-5 py-2.5 rounded-sm font-bold text-xs uppercase tracking-wider inline-flex items-center gap-2 transition-all ${
                      allReady
                        ? 'bg-gradient-to-r from-[#FF5E1A] to-emerald-500 hover:from-[#FF7A42] hover:to-emerald-400 text-white shadow-[0_0_28px_rgba(255,94,26,0.4)]'
                        : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                    }`}
                    data-testid="wizard-run-analysis"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    Run Baseline Analysis
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </>
          )}

          {/* ═══ SUBMITTING PHASE ═══ */}
          {phase === 'submitting' && (
            <div className="py-6 space-y-4" data-testid="submitting-phase">
              <div className="flex items-center gap-3">
                <Loader2 className="w-6 h-6 text-[#FF5E1A] animate-spin flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-white font-bold text-sm">
                    {uploadPct < 92 ? 'Uploading your files…' : 'Analyzing your ECU + datalogs…'}
                  </p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">
                    {uploadPct < 92
                      ? 'Files are streaming to our AI backend. Do not close this window.'
                      : 'Running fingerprint, map discovery, health-gate, and HP estimation. This takes 5–20 seconds.'}
                  </p>
                </div>
              </div>
              <ProgressBar pct={uploadPct} />
              <div className="grid grid-cols-3 gap-2">
                {STEPS.map((s, i) => (
                  <div
                    key={s.key}
                    className={`p-2 rounded-sm border text-[10px] font-['JetBrains_Mono'] ${
                      uploadPct >= (i + 1) * 30
                        ? 'border-emerald-500/40 bg-emerald-500/5 text-emerald-300'
                        : 'border-zinc-800 bg-[#0a0a0a] text-zinc-600'
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      {uploadPct >= (i + 1) * 30 ? <Check className="w-3 h-3" /> : <Loader2 className="w-3 h-3 animate-spin" />}
                      <span className="truncate uppercase">{s.title.split(' ')[0]}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ═══ REPORT PHASE ═══ */}
          {phase === 'report' && report && (
            <>
              <CompactSummary report={report} />

              {showFullReport && <FullReport report={report} />}

              {/* CTA block */}
              <div className="bg-gradient-to-br from-[#FF5E1A]/15 via-transparent to-emerald-500/10 border border-[#FF5E1A]/40 rounded-sm p-4 sm:p-5 space-y-3">
                <h4 className="text-white font-['Outfit'] font-bold text-base sm:text-lg">Ready to actually tune it?</h4>
                <p className="text-xs sm:text-sm text-zinc-400 leading-relaxed">
                  The baseline is the start. Sign up free to unlock iterative AI tuning with real binary generation,
                  live co-pilot chat, map viewer, per-tune history, and unlimited tune versions.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 pt-1">
                  <button
                    onClick={() => { reset(); onClose(); onSignUpCta?.(); }}
                    className="flex-1 px-5 py-3 bg-[#FF5E1A] hover:bg-[#FF7A42] text-white font-bold text-xs uppercase tracking-wider rounded-sm inline-flex items-center justify-center gap-2 shadow-[0_0_28px_rgba(255,94,26,0.4)] transition-all hover:-translate-y-0.5"
                    data-testid="free-analysis-signup-cta"
                  >
                    Create Free Account to Continue AI Tuning
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShowFullReport((v) => !v)}
                    className="px-5 py-3 border border-emerald-500/50 hover:border-emerald-400 hover:bg-emerald-500/10 text-emerald-300 font-bold text-xs uppercase tracking-wider rounded-sm inline-flex items-center justify-center gap-2 transition-all"
                    data-testid="view-full-report-cta"
                  >
                    <FileText className="w-4 h-4" />
                    {showFullReport ? 'Hide Full Report' : 'View Full Report'}
                  </button>
                </div>
              </div>

              <button
                onClick={reset}
                className="text-xs text-zinc-500 hover:text-white transition-colors"
                data-testid="free-analysis-reset"
              >
                ← Analyze another car
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
